import type { PoolStat, PoolStats } from './types';

// DB connection pool — relay-side Postgres pool utilization, the system-plumbing
// companion to the feed transport above. The whole point of the 2026-07-01 fix
// lives here: the ORDER PATH runs on the isolated `critical` pool, so even when
// the `shared` pool (reads / pollers / whale-ledger folds) saturates at an event
// burst, placement still gets a connection. `waiting` > 0 = that pool is at cap
// and requests are queuing. Per-lane, like the rest of this page.
export function poolTone(inUse: number, max: number, waiting: number): string {
  if (waiting > 0 || (max > 0 && inUse >= max)) return 'text-red-400';
  if (max > 0 && inUse >= max * 0.75) return 'text-amber-400';
  return 'text-green-400';
}
function PoolRow({ label, desc, s }: { label: string; desc: string; s: PoolStat | null }) {
  if (!s) {
    return (
      <tr className="border-border/50 border-b">
        <td className="text-foreground py-1.5 pr-3">{label}</td>
        <td className="text-muted-foreground px-3 text-[11px]" colSpan={5}>
          not initialized yet (no query has hit this pool)
        </td>
      </tr>
    );
  }
  const pct = s.max > 0 ? Math.round((s.inUse / s.max) * 100) : 0;
  const tone = poolTone(s.inUse, s.max, s.waiting);
  const bar =
    s.waiting > 0 || (s.max > 0 && s.inUse >= s.max)
      ? 'bg-red-500'
      : s.max > 0 && s.inUse >= s.max * 0.75
        ? 'bg-amber-500'
        : 'bg-green-500';
  return (
    <tr className={`border-border/50 border-b ${s.waiting > 0 ? 'bg-red-500/10' : ''}`}>
      <td className="py-1.5 pr-3">
        <span className="text-foreground">{label}</span>
        <span className="text-muted-foreground ml-2 text-[10px]">{desc}</span>
      </td>
      <td className="px-3 text-right">
        <span className={`font-semibold ${tone}`}>{s.inUse}</span>
        <span className="text-muted-foreground">/{s.max}</span>
      </td>
      <td className="text-muted-foreground px-3 text-right">{s.idle}</td>
      <td className={`px-3 text-right ${s.waiting > 0 ? 'font-semibold text-red-400' : 'text-muted-foreground'}`}>
        {s.waiting}
      </td>
      <td className="px-3">
        <div className="bg-muted h-2 w-full overflow-hidden rounded" title={`${s.inUse} of ${s.max} in use`}>
          <div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </td>
      <td className={`px-3 text-right font-mono ${tone}`}>{pct}%</td>
    </tr>
  );
}
export function DbPool({ stats }: { stats?: PoolStats }) {
  if (!stats) return null;
  const openTotal = (stats.shared?.total ?? 0) + (stats.critical?.total ?? 0);
  const anyWaiting = (stats.shared?.waiting ?? 0) + (stats.critical?.waiting ?? 0) > 0;
  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">DB connection pool</h2>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
            anyWaiting ? 'bg-red-600/80 text-white' : 'bg-green-600/80 text-white'
          }`}
        >
          {anyWaiting ? 'QUEUING' : 'HEALTHY'}
        </span>
        <span className="text-muted-foreground text-[11px]">
          this relay&apos;s Postgres pools · the order path runs on the isolated{' '}
          <span className="text-foreground">critical</span> pool so a read storm on{' '}
          <span className="text-foreground">shared</span> can&apos;t starve placement · waiting &gt; 0 = at cap
        </span>
      </div>
      <table className="w-full text-left text-xs font-mono">
        <thead className="text-muted-foreground border-border border-b">
          <tr>
            <th className="py-1.5 pr-3 font-medium">Pool</th>
            <th className="px-3 text-right font-medium" title="checked out right now / configured max">In use</th>
            <th className="px-3 text-right font-medium" title="idle open connections, ready for instant checkout">Idle</th>
            <th
              className="px-3 text-right font-medium"
              title="requests queued for a connection — >0 means this pool is saturated"
            >
              Waiting
            </th>
            <th className="px-3 font-medium">Utilization</th>
            <th className="px-3 text-right font-medium" aria-label="percent" />
          </tr>
        </thead>
        <tbody>
          <PoolRow label="shared" desc="reads · pollers · ledger folds" s={stats.shared} />
          <PoolRow label="critical" desc="order path — auth · limits · logTrade" s={stats.critical} />
        </tbody>
      </table>
      <p className="text-muted-foreground mt-2 text-[11px]">
        {openTotal} open of server max_connections={stats.serverMax} (shared with the box&apos;s other tenants).
        Reflects this build&apos;s relay (g1.pm = staging; gbuff = live). Watch for{' '}
        <span className="text-foreground">shared</span> waiting climbing while{' '}
        <span className="text-foreground">critical</span> stays flat — that&apos;s reads degrading while orders keep
        flowing, i.e. the pool-isolation fix doing its job.
      </p>
    </section>
  );
}
